import React, { useEffect, useMemo, useRef, useState, useCallback} from "react";
import { createPortal } from "react-dom";

const VALOR_TODAS = "todas";
const TEXTO_TODAS = "Todas las opciones";

function crearOpcionNormalizada(entrada, idPorDefecto) {
	if (entrada && typeof entrada === "object" && !Array.isArray(entrada)) {
		const id = entrada.id ?? entrada.valor ?? idPorDefecto;

		return {
			id,
			texto: entrada.texto ?? entrada.etiqueta ?? entrada.nombre ?? String(id),
		};
	}

	if (Array.isArray(entrada)) {
		return {
			id: idPorDefecto,
			texto: String(entrada[0] ?? idPorDefecto),
		};
	}

	return {
		id: entrada ?? idPorDefecto,
		texto: String(entrada ?? idPorDefecto),
	};
}

function normalizarOpciones(opciones) {
	if (Array.isArray(opciones)) {
		return opciones.map((opcion, indice) =>
			crearOpcionNormalizada(opcion, indice),
		);
	}

 	return Object.keys(opciones || {}).map((clave) =>
		crearOpcionNormalizada(opciones[clave], clave),
	);
}

function normalizarSeleccion(seleccion, incluirOpcionTodas, seleccionUnica) {
	let seleccionNormalizada = Array.isArray(seleccion)
		? [...new Set(seleccion)].filter(
				(valorSeleccionado) =>
					incluirOpcionTodas || valorSeleccionado !== VALOR_TODAS,
			)
		: [];

	if (seleccionUnica) {
		seleccionNormalizada = seleccionNormalizada.slice(0, 1);
	}

	if (seleccionNormalizada.length > 0) {
		return seleccionNormalizada;
	}

	return incluirOpcionTodas ? [VALOR_TODAS] : [];
}

function sonSeleccionesIguales(a = [], b = []) {
	if (a.length !== b.length) {
		return false;
	}

	return a.every((valor, indice) => valor === b[indice]);
}

export default function SelectorMultiple({
	opciones = [], // lista de opciones: [{ id: 1, texto: "Madrid" }]
	valor, // valor seleccionado: [1]
	valorInicial, // valor inicial: [1]
	alAplicar, // lo que se hace al aplicar, una funcion: (seleccion) => guardar(seleccion)
	alCambiar, // lo que se hace al cambiar en el panel, una funcion: (seleccionTemporal) => console.log(seleccionTemporal)
	filtrarOpcion, // filtro opcional para cada opcion, una funcion: (opcion) => opcion.id !== 0
	textoTodas = TEXTO_TODAS, // texto para la opcion de todas
	placeholderBusqueda = "Buscar...", // texto del buscador: "Buscar..."
	textoBoton = "Aplicar", // texto del boton principal: "Guardar"
	deshabilitado = false, // deshabilitado o no
	seleccionUnica = false, // permite una sola seleccion o no
	incluirOpcionTodas = true, // muestra la opcion  todas
	aplicarAlSeleccionar = false, // aplica al pulsar una opcion sin boton: true
	placeholderResumen = "Selecciona una opcion", // texto cuando no hay opcion seleccionada
	mensajeSinResultados = "No hay resultados para la busqueda actual.", //texto para cuando se busca y no sale nada
}) {
	const esControlado = valor !== undefined;
	const contenedorRef = useRef(null);
	const disparadorRef = useRef(null);
	const panelRef = useRef(null);
	const seAplicaAlSeleccionar = seleccionUnica && aplicarAlSeleccionar;
	const [posicionPanel, setPosicionPanel] = useState({ top: 0, left: 0, width: 0 });
	const tipoInput = seleccionUnica ? "radio" : "checkbox";

	const seleccionBase = useMemo(
		() =>
			normalizarSeleccion(
				esControlado ? valor : valorInicial,
				incluirOpcionTodas,
				seleccionUnica,
			),
		[esControlado, incluirOpcionTodas, seleccionUnica, valor, valorInicial],
	);

	const opcionesNormalizadas = useMemo(
		() => normalizarOpciones(opciones),
		[opciones],
	);

	const [seleccionAplicada, setSeleccionAplicada] = useState(() =>
		seleccionBase,
	);
	const [seleccionTemporal, setSeleccionTemporal] = useState(() =>
		seleccionBase,
	);
	const [busqueda, setBusqueda] = useState("");
	const [abierto, setAbierto] = useState(false);

	useEffect(() => {
		setSeleccionAplicada((anterior) =>
			sonSeleccionesIguales(anterior, seleccionBase) ? anterior : seleccionBase,
		);
		setSeleccionTemporal((anterior) =>
			sonSeleccionesIguales(anterior, seleccionBase) ? anterior : seleccionBase,
		);
	}, [seleccionBase]);

	const cerrarPanel = useCallback(() => {
		setSeleccionTemporal(seleccionAplicada);
		setBusqueda("");
		setAbierto(false);
	}, [seleccionAplicada]);

	useEffect(() => {
		if (!abierto || !disparadorRef.current) return;
		const rect = disparadorRef.current.getBoundingClientRect();
		setPosicionPanel({ top: rect.bottom + 4, left: rect.left, width: rect.width });
	}, [abierto]);

	useEffect(() => {
		if (!abierto) {
			return undefined;
		}

		function gestionarClickFuera(evento) {
			if (
				!contenedorRef.current?.contains(evento.target) &&
				!panelRef.current?.contains(evento.target)
			) {
				cerrarPanel();
			}
		}

		document.addEventListener("mousedown", gestionarClickFuera);
		return () => document.removeEventListener("mousedown", gestionarClickFuera);
	}, [abierto, seleccionAplicada, cerrarPanel]);

	const opcionesVisibles = useMemo(() => {
		const texto = busqueda.toLowerCase();

		return opcionesNormalizadas
			.filter((opcion) => (filtrarOpcion ? filtrarOpcion(opcion) : true))
			.sort((a, b) => a.texto.localeCompare(b.texto, "es"))
			.filter((opcion) => opcion.texto.toLowerCase().includes(texto));
	}, [busqueda, filtrarOpcion, opcionesNormalizadas]);

	const mostrarOpcionTodas =
		incluirOpcionTodas &&
		textoTodas.toLowerCase().includes(busqueda.toLowerCase());

	const textoResumen = useMemo(() => {
		if (incluirOpcionTodas && seleccionAplicada.includes(VALOR_TODAS)) {
			return textoTodas;
		}

		const textosSeleccionados = opcionesNormalizadas
			.filter((opcion) => seleccionAplicada.includes(opcion.id))
			.map((opcion) => opcion.texto);

		if (textosSeleccionados.length === 0) {
			return placeholderResumen;
		}

		return seleccionUnica
			? textosSeleccionados[0]
			: textosSeleccionados.join(", ");
	}, [
		incluirOpcionTodas,
		opcionesNormalizadas,
		placeholderResumen,
		seleccionAplicada,
		seleccionUnica,
		textoTodas,
	]);

	function aplicarSeleccionConValor(nuevaSeleccion) {
		setSeleccionAplicada(nuevaSeleccion);

		if (alAplicar) {
			alAplicar(nuevaSeleccion);
		}
	}

	function actualizarSeleccionTemporal(nuevaSeleccion) {
		setSeleccionTemporal(nuevaSeleccion);

		if (alCambiar) {
			alCambiar(nuevaSeleccion);
		}
	}

	function cambiarOpcion(idOpcion) {
		let nuevaSeleccion = [...seleccionTemporal];

		if (idOpcion === VALOR_TODAS) {
			nuevaSeleccion = [VALOR_TODAS];
		} else if (seleccionUnica) {
			nuevaSeleccion = [idOpcion];
		} else {
			if (nuevaSeleccion.includes(idOpcion)) {
				nuevaSeleccion = nuevaSeleccion.filter((id) => id !== idOpcion);
			} else {
				nuevaSeleccion.push(idOpcion);
			}

			if (nuevaSeleccion.length === 0 && incluirOpcionTodas) {
				nuevaSeleccion = [VALOR_TODAS];
			}

			if (nuevaSeleccion.length > 1 && nuevaSeleccion.includes(VALOR_TODAS)) {
				nuevaSeleccion = nuevaSeleccion.filter((id) => id !== VALOR_TODAS);
			}
		}

		actualizarSeleccionTemporal(nuevaSeleccion);

		if (seAplicaAlSeleccionar) {
			aplicarSeleccionConValor(nuevaSeleccion);
			setBusqueda("");
			setAbierto(false);
		}
	}

	function aplicarSeleccion() {
		aplicarSeleccionConValor(seleccionTemporal);
		setBusqueda("");
		setAbierto(false);
	}

	function alternarPanel() {
		if (abierto) {
			cerrarPanel();
			return;
		}

		setSeleccionTemporal(seleccionAplicada);
		setAbierto(true);
	}

	return (
		<div className="sm-contenedor" ref={contenedorRef}>
			<button
				type="button"
				disabled={deshabilitado}
				onClick={alternarPanel}
				className="sm-disparador"
				ref={disparadorRef}
			>
				{textoResumen}
			</button>

			{abierto && createPortal(
				<div className="sm-panel" ref={panelRef} style={{ top: posicionPanel.top, left: posicionPanel.left, width: posicionPanel.width }}>
					<input
						type="text"
						placeholder={placeholderBusqueda}
						value={busqueda}
						onChange={(e) => setBusqueda(e.target.value)}
						disabled={deshabilitado}
						className="sm-buscador"
					/>

					<div className="sm-lista">
						{mostrarOpcionTodas && (
							<label className="sm-opcion">
								<input
									type={tipoInput}
									checked={seleccionTemporal.includes(VALOR_TODAS)}
									onChange={() => cambiarOpcion(VALOR_TODAS)}
									disabled={deshabilitado}
								/>
								<span>{textoTodas}</span>
							</label>
						)}

						{opcionesVisibles.map((opcion) => (
							<label className="sm-opcion" key={opcion.id}>
								<input
									type={tipoInput}
									checked={seleccionTemporal.includes(opcion.id)}
									onChange={() => cambiarOpcion(opcion.id)}
									disabled={deshabilitado}
								/>
								<span>{opcion.texto}</span>
							</label>
						))}

						{opcionesVisibles.length === 0 && !mostrarOpcionTodas && (
							<p className="sm-sin-resultados">{mensajeSinResultados}</p>
						)}
					</div>

					{!seAplicaAlSeleccionar && (
						<button
							type="button"
							onClick={aplicarSeleccion}
							disabled={deshabilitado}
							className="button primary"
						>
							{textoBoton}
						</button>
					)}
				</div>,
				document.body
			)}

			<input
				type="hidden"
				value={seleccionAplicada.join(",")}
				readOnly
				aria-hidden="true"
			/>
		</div>
	);
}
